/**
 * localStorage access that never throws.
 *
 * Some browsers deny `window.localStorage` entirely (SecurityError /
 * DOMException code 18) — common on mobile Chrome with restricted storage,
 * privacy settings, or embedded/iframe contexts. Bare `localStorage.*` calls
 * then crash route guards (see MOVEPRO-BOOK-A8).
 *
 * When the real store is unavailable we fall back to an in-memory Map so
 * VueUse `useStorage` and session flags keep working for the current page
 * lifetime (state is not persisted across reloads in that case).
 */

let cached = null;

function createMemoryStorage() {
    const map = new Map();

    return {
        get length() {
            return map.size;
        },
        clear() {
            map.clear();
        },
        getItem(key) {
            return map.has(key) ? map.get(key) : null;
        },
        setItem(key, value) {
            map.set(String(key), String(value));
        },
        removeItem(key) {
            map.delete(key);
        },
        key(index) {
            return Array.from(map.keys())[index] ?? null;
        },
    };
}

function probeStorage(storage) {
    const probeKey = '__mp_storage_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
}

/**
 * Resolve a Storage-compatible backend. Prefer real localStorage; fall back
 * to memory when reading or probing localStorage throws.
 *
 * @param {Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'clear' | 'key' | 'length'> | null | undefined} [storageOverride]
 *        optional Storage for tests (`null` forces memory)
 */
export function getSafeLocalStorage(storageOverride) {
    if (storageOverride !== undefined) {
        if (storageOverride === null) {
            return createMemoryStorage();
        }

        try {
            probeStorage(storageOverride);
            return storageOverride;
        } catch {
            return createMemoryStorage();
        }
    }

    if (cached) {
        return cached;
    }

    try {
        const storage = globalThis.localStorage;
        if (!storage) {
            cached = createMemoryStorage();
            return cached;
        }
        probeStorage(storage);
        cached = storage;
        return cached;
    } catch {
        cached = createMemoryStorage();
        return cached;
    }
}

/** Reset the module cache — tests only. */
export function resetSafeLocalStorageCache() {
    cached = null;
}

export function safeLocalStorageGet(key) {
    try {
        return getSafeLocalStorage().getItem(key);
    } catch {
        return null;
    }
}

export function safeLocalStorageSet(key, value) {
    try {
        getSafeLocalStorage().setItem(key, value);
    } catch {
        // ignore — denied or full storage
    }
}

export function safeLocalStorageRemove(key) {
    try {
        getSafeLocalStorage().removeItem(key);
    } catch {
        // ignore — denied storage
    }
}
